import { ApiError, ForbiddenError } from '@lib/api';

import type { ComponentWriteCopy } from './component-error-copy';

/**
 * The words for the refusals `POST /release-components` can give, branched on
 * `code` for the reason `component-error-copy.ts` gives.
 *
 * Kept apart from the commercial copy because the same situation reads
 * differently: a flag a release names that does not exist is simply a missing
 * flag. Nothing here mentions entitlements — a release takes flags of any
 * purpose, so that rule would be false.
 *
 * Returns `null` for what the shared writer already words (a 403, a conflict, a
 * validation list) and for any code it does not know.
 */
export function explainReleaseWriteFailure(error: Error): ComponentWriteCopy | null {
    if (!(error instanceof ApiError) || error instanceof ForbiddenError) return null;

    switch (error.code) {
        case 'RELEASE_MEMBER_NOT_FOUND':
            return {
                title: 'One of those flags does not exist',
                body: error.message,
                detail: 'Check the key for a typo. A flag has to exist in the catalog before a release can name it.',
            };

        case 'RELEASE_MEMBER_ARCHIVED':
            return {
                title: 'One of those flags is archived',
                body: error.message,
                detail: 'An archived flag cannot be added to a release. Remove it from the list, or restore the flag first.',
            };

        case 'RELEASE_ALREADY_EXISTS':
            return {
                title: 'That release id is taken',
                body: error.message,
                detail: 'Pick another id. Release ids share a namespace with components and are never reused, so nothing here can be overwritten.',
            };

        case 'INVALID_RELEASE_ID':
            return {
                title: 'That release id is not usable',
                body: error.message,
                detail: "Use 1-64 characters of A-Z, a-z, 0-9, '_' or '-'. It becomes part of the page address.",
            };

        case 'INVALID_RELEASE_VERSION':
            return {
                title: 'That release version is not usable',
                body: error.message,
                detail: "Use 1-32 characters of A-Z, a-z, 0-9, '.', '_' or '-', for example 2026.10.1.",
            };

        case 'RELEASE_TOO_MANY_FLAGS':
            return {
                title: 'Too many flags in one release',
                body: error.message,
                detail: 'A release holds at most 50 flags. Split it into two releases, or drop the flags that are not part of this release.',
            };

        case 'RELEASE_ROW_VERSION_CONFLICT':
            return {
                title: 'This release changed while you were editing',
                body: error.message,
                detail: 'Someone else saved it first. Reload the page to see the current version and flags, then make your change again.',
            };

        case 'RELEASE_RETIRED':
            return {
                title: 'This release is archived',
                body: error.message,
                detail: 'An archived release cannot be edited or archived again. It no longer appears in the list.',
            };

        case 'RELEASE_OPERATION_IN_PROGRESS':
            return {
                title: 'An operation is in progress',
                body: error.message,
                detail: 'A release cannot be archived or turned on or off while an operation is running on it. Wait for it to finish, then try again.',
            };

        case 'RELEASE_HAS_NO_FLAGS':
            return {
                title: 'This release has no flags',
                body: error.message,
                detail: 'Add at least one flag to the release, then turn it on.',
            };

        case 'RELEASE_OPERATION_ENQUEUE_INCOMPLETE':
            return {
                title: 'The operation did not fully start',
                body: error.message,
                detail: 'It is shown as stalled on this page. Flags already applied stay applied.',
            };

        default:
            return null;
    }
}
